/**
 * Apple hardware identifiers (utsname machine) → marketing names for CRM display.
 * Data is synced from upstream sources — run `npm run sync:apple-devices` to refresh.
 * @see scripts/sync-apple-device-identifiers.js
 */

import deviceCatalog from "../data/appleDeviceIdentifiers.json";

/** @type {Record<string, string>} */
const APPLE_DEVICE_MODEL_NAMES = deviceCatalog.identifiers || {};

function normalizeHardwareId(raw) {
  return String(raw || "").trim();
}

function isHardwareIdentifier(id) {
  return /^(iPhone|iPad|iPod|Watch|Bridge|iProd)\d/i.test(id);
}

function fallbackLabelForHardwareId(id) {
  if (/^iPhone/i.test(id)) return "Unknown iPhone";
  if (/^iPad/i.test(id)) return "Unknown iPad";
  if (/^iPod/i.test(id)) return "Unknown iPod";
  if (/^Watch/i.test(id)) return "Unknown Apple Watch";
  return id;
}

/** @returns {string} Marketing name, unknown-family label, or original value if already friendly. */
export function formatAppleDeviceModel(raw) {
  const id = normalizeHardwareId(raw);
  if (!id) return "";
  if (APPLE_DEVICE_MODEL_NAMES[id]) return APPLE_DEVICE_MODEL_NAMES[id];
  // Already a friendly name from GA4 or manual entry
  if (/^iPhone \d/i.test(id) || /^iPhone SE/i.test(id) || /^iPhone Air/i.test(id)) return id;
  if (/^iPad /i.test(id)) return id;
  if (isHardwareIdentifier(id)) return fallbackLabelForHardwareId(id);
  return id;
}

/** True when the resolved marketing name indicates iPhone Pro / Pro Max. */
export function isIphoneProModel(raw) {
  const id = normalizeHardwareId(raw);
  if (!id || /^iPad/i.test(id)) return false;
  const label = formatAppleDeviceModel(id);
  if (/^iPad/i.test(label)) return false;
  return /^iPhone/i.test(label) && /\bPro\b/i.test(label);
}

/** True when the resolved marketing name indicates iPad Pro. */
export function isIpadProModel(raw) {
  const id = normalizeHardwareId(raw);
  if (!id) return false;
  const label = formatAppleDeviceModel(id);
  return /\biPad Pro\b/i.test(label);
}

/** Count of synced identifiers (for diagnostics). */
export function getAppleDeviceCatalogSize() {
  return Object.keys(APPLE_DEVICE_MODEL_NAMES).length;
}
